// Shared pieces of the P1 pages (CAT-C-05): queries, refusal wording, money, the open tab in the address.
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Refusal } from '../../data/auth';
import {
  loadBrands,
  loadCategories,
  loadCosts,
  loadCustomerGroups,
  loadCustomers,
  loadPrices,
  loadProducts,
  loadSuppliers,
  photoLinks,
} from '../../data/catalog';
import type { Language } from '../../i18n';
import { messageKey } from '../../lib/errors';
import { formatMoney } from '../../lib/format';
import { useSession } from '../../app/session';

export const CATALOG = ['catalog'] as const;

export function useCatalogData() {
  const { role } = useSession();
  const products = useQuery({ queryKey: [...CATALOG, 'products'], queryFn: loadProducts });
  const categories = useQuery({ queryKey: [...CATALOG, 'categories'], queryFn: loadCategories });
  const brands = useQuery({ queryKey: [...CATALOG, 'brands'], queryFn: loadBrands });
  // ADR-088 / GEN-15: the Storekeeper never asks for prices; only the owner asks for costs.
  const prices = useQuery({ queryKey: [...CATALOG, 'prices'], queryFn: loadPrices, enabled: role !== 'storekeeper' });
  const costs = useQuery({ queryKey: [...CATALOG, 'costs'], queryFn: loadCosts, enabled: role === 'owner' });
  return { products, categories, brands, prices, costs };
}

/** PRD-12: the photo link of any product by id (stock tiles), from the shared products query. */
export function useProductPhoto(): (productId: string) => string | undefined {
  const products = useQuery({ queryKey: [...CATALOG, 'products'], queryFn: loadProducts });
  const path = new Map((products.data ?? []).map((p) => [p.id, p.image_path]));
  const links = usePhotoLinks([...path.values()]);
  return (id) => {
    const p = path.get(id);
    return p ? links?.get(p) : undefined;
  };
}

/** PRD-12: signed links for the products' photos (one request; refreshed before the links expire). */
export function usePhotoLinks(paths: (string | null | undefined)[]) {
  const list = [...new Set(paths.filter((p): p is string => !!p))].sort();
  return useQuery({
    queryKey: [...CATALOG, 'photos', list],
    queryFn: () => photoLinks(list),
    enabled: list.length > 0,
    staleTime: 50 * 60_000,
  }).data;
}
export function useSuppliers() {
  return useQuery({ queryKey: [...CATALOG, 'suppliers'], queryFn: loadSuppliers });
}
export function useCustomers() {
  return useQuery({ queryKey: [...CATALOG, 'customers'], queryFn: loadCustomers });
}
export function useCustomerGroups() {
  return useQuery({ queryKey: [...CATALOG, 'groups'], queryFn: loadCustomerGroups });
}

/** The open tab lives in the address (?tab=), so a refresh or a link returns to it (ADR-087). */
export function useTab<T extends string>(tabs: readonly T[], fallback: T): [T, (tab: T) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab = tabs.includes(raw as T) ? (raw as T) : fallback;
  const set = useCallback(
    (next: T) =>
      setParams(
        (p) => {
          p.set('tab', next);
          return p;
        },
        { replace: true },
      ),
    [setParams],
  );
  return [tab, set];
}

export function useMoney() {
  const { i18n } = useTranslation();
  const lang = i18n.language as Language;
  return (value: number | null | undefined) => (value === null || value === undefined ? '' : formatMoney(value, lang));
}

/** The words for a refusal. `nameTaken` words NAME_TAKEN for the record at hand; details fill {p} and {c}. */
export function useRefusalText() {
  const { t } = useTranslation();
  return (err: unknown, nameTaken?: string) => {
    const code = err instanceof Refusal ? err.code : 'SERVER_ERROR';
    if (code === 'NAME_TAKEN' && nameTaken) return t(nameTaken);
    const detail = err instanceof Refusal ? err.detail : {};
    // The phone belongs to a private customer of someone else: say so without naming it.
    if (code === 'PHONE_TAKEN' && !detail.customer) return t('err.phone.other');
    return t(messageKey(code), { p: String(detail.product ?? ''), c: String(detail.customer ?? '') });
  };
}
export const refusalCode = (err: unknown) => (err instanceof Refusal ? err.code : 'SERVER_ERROR');

/** Two initials for an avatar; the Arabic article «ال» is skipped (بقالة السوق → بس). */
export function initials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .map((w) => (w.length > 3 && w.startsWith('ال') ? w.slice(2) : w));
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase();
}

/** Parses a typed amount ("1 450,50", "1,450.50", "1450.5") to a number, or null. */
export function parseAmount(text: string): number | null {
  const clean = text.replace(/\s/g, ''); // \s also matches the no-break spaces of French amounts
  if (!clean) return null;
  const normalized = clean.includes(',') && !clean.includes('.') ? clean.replace(',', '.') : clean.replace(/,/g, '');
  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
}
export function parseWhole(text: string): number | null {
  if (!text.trim()) return null;
  const value = Number(text.trim());
  return Number.isInteger(value) ? value : NaN;
}
